import React, { useEffect, useRef, useState } from 'react'
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import GuestPicker from './GuestPicker';
import AreaPicker from './AreaPicker';
import GuestDetails from './GuestDetails';
import BookingSummary from './BookingSummary';
import AlternatePicker from './AlternatePicker';
import NoBookingsAvailable from './NoBookingsAvailable';
import CreditCardCapture from './CreditCardCapture';
import GuestEnquiryDetails from './GuestEnquiryDetails';
import EnquiryConfirmation from './EnquiryConfirmation';
import { findEnquiryOccasion } from './enquiryOccasionHelper';
import { addMonths, format, parse } from 'date-fns';
import { BookingTimeModel } from './BookingTimeModel';

/**
 * Convert/map the JSON Schedule object from the ViewModel into a simple Key value list of arrays of timeslots
 */
const mapSchedule = (schedule) => {
  if (!schedule) return schedule;

  const mappedSchedule = {}

  for (const key in schedule) {
    mappedSchedule[format(parse(key, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')] = schedule[key].timeSlots;
  }

  return mappedSchedule;

}

const BookingStart = ({ bookingStartInfo }) => {

    const defaultBookingErrorMessage = "Booking failed, please try again or contact the restaurant directly.";
    const defaultEnquiryErrorMessage = "Sending enquiry failed. Please try again or contact the restaurant directly.";
  const [dateInfo, setDateInfo] = useState({});
  const [timeInfo, setTimeInfo] = useState({});
  const [hours, setHours] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [createEnquiryError, setCreateEnquiryError] = useState(null);
  const [stepAnnouncement, setStepAnnouncement] = useState('');

  const STEP_LABELS = {
    1: 'Step 1 of 3: Choose date, time and guests',
    2: 'Step 2 of 3: Your details',
    4: 'Step 1 of 3: Alternative options',
    5: 'No availability: Enquiry options',
    6: 'Step 3 of 3: Payment',
    7: 'Step 2 of 3: Enquiry details',
    8: 'Enquiry submitted',
  };

  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step4Ref = useRef(null);
  const step5Ref = useRef(null);
  const step6Ref = useRef(null);
  const step7Ref = useRef(null);
  const step8Ref = useRef(null);
  const stepRefs = { 1: step1Ref, 2: step2Ref, 4: step4Ref, 5: step5Ref, 6: step6Ref, 7: step7Ref, 8: step8Ref };

  const guestNumberHelperText = bookingStartInfo.guestNumberHelperText || null;
  const enableCustomField = !!bookingStartInfo.enableCustomField || false;
  const customFieldLabel = bookingStartInfo.customFieldLabel || null;

  //
  //  initialize the booking time model that contains
  //  business logic for date/time selection
  const bookingTimeModel = useRef(new BookingTimeModel(
    new Date(),
    addMonths(new Date(), 6),
    bookingStartInfo.openingTimes,
    bookingStartInfo.openingHours,
    parse(bookingStartInfo.firstAvailableDay, 'yyyy-MM-dd', new Date()),
    mapSchedule(bookingStartInfo.schedule),
    bookingStartInfo.overrideOpeningTime,
    bookingStartInfo.overrideClosingTime
  )).current;

  const setStatefromBookingTimeModel = () => {
    // No bookable day was found (e.g. an occasion/restaurant with no availability).
    // bookingTimeModel.date would be an Invalid Date (parseDate(null)) and the time
    // calculations (getHours -> mergeTimeWithDate -> toISOString) would throw
    // "Invalid time value". Show no times instead of crashing the booking widget.
    if (!bookingTimeModel.day) {
      setHours([]);
      setMinutes([]);
      return;
    }
    setDateInfo((dateInfo) => ({
      ...dateInfo,
      selectedDay: bookingTimeModel.date.getDate(),
      selectedMonth: bookingTimeModel.date.getMonth(),
      selectedYear: bookingTimeModel.date.getFullYear(),
      mainSchedule: bookingTimeModel.mainSchedule
    }));

    setTimeInfo(() => ({
      selectedHour: bookingTimeModel.time.split(':')[0],
      selectedMinute: bookingTimeModel.time.split(':')[1]
    }));

    setHours(bookingTimeModel.getHours(
      new Date(),
      bookingTimeModel.date
    ));

    setMinutes(bookingTimeModel.getMinutes(
      new Date(),
      bookingTimeModel.date,
      parseInt(bookingTimeModel.time?.split(':')[0])
    ));

  }



  useEffect(() => {

    if (bookingStartInfo.urlDate && bookingStartInfo.urlTime) {
      const bookingStartDate = parse(bookingStartInfo.urlDate, 'yyyy-MM-dd', new Date());
      const time = bookingStartInfo.urlTime.substring(0, 2) + ':' + bookingStartInfo.urlTime.substring(2, 4)

      if (bookingStartDate >= new Date()) {
        bookingTimeModel.select(
          format(bookingStartDate, 'dd/MM/yyyy'),
          time
        );
      }
    }

    setStatefromBookingTimeModel();

  }, []);

  const [isCheckingAvailability, setCheckingAvailability] = useState(false);
  const [restaurantInfo, setRestaurantInfo] = useState({});


  const [guestInfo, setGuestInfo] = useState({});
  const [areaInfo, setAreaInfo] = useState({});
  const [bookingProcess, setBookingProcess] = useState({});
  const [availabilityOptions, setAvailabilityOptions] = useState({});
  const [createBookingMessage, setCreateBookingMessage] = useState(null);
  const [occasionBooking, setOccasionBooking] = useState({});
  const [isOccasionAvaliablity, setOccasionAvaliablity] = useState(true);


  useEffect(() => {
    window.scrollTo(0, 0);
    if (bookingProcess.step) {
      setStepAnnouncement(STEP_LABELS[bookingProcess.step] ?? 'Booking step updated');
      stepRefs[bookingProcess.step]?.current?.focus();
    }
  }, [bookingProcess.step])

  useEffect(() => {
    if (!!bookingStartInfo.occasionBooking) {
      setOccasionBooking(bookingStartInfo.occasionBooking);
    }
    getOccasionAvaliablity();
    document.dispatchEvent(new CustomEvent('booking:ready'));
  }, [])

  const getOccasionAvaliablity = () => {
    let today = new Date();
    if (!!bookingStartInfo.occasionBooking) {
      const occasion = bookingStartInfo.occasionBooking;
      var occasionFrom = new Date(parseInt(occasion?.occasionAvaliableFrom?.year), parseInt(occasion?.occasionAvaliableFrom?.month), parseInt(occasion?.occasionAvaliableFrom?.day));
      var occasionTo = new Date(parseInt(occasion?.occasionAvaliableTo?.year), parseInt(occasion?.occasionAvaliableTo?.month), parseInt(occasion?.occasionAvaliableTo?.day));
      if (today > occasionFrom || today < occasionTo) {
        setOccasionAvaliablity(true);
      } else {
        setOccasionAvaliablity(false);
      }
    }
  }

  useEffect(() => {
    const getSiteInfoId = async () => {
      var restaurantInfo = {
        restaurantName: bookingStartInfo.name,
        image: bookingStartInfo.imageUrl,
        imageAlt: bookingStartInfo.imageAlt,
        address: bookingStartInfo.address,
        openingTimes: [
          { sunday: { IsOpen: bookingStartInfo.openingTimes.Sunday.IsOpen, Open: bookingStartInfo.openingTimes.Sunday.Open, Closed: bookingStartInfo.openingTimes.Sunday.Closed } },
          { monday: { IsOpen: bookingStartInfo.openingTimes.Monday.IsOpen, Open: bookingStartInfo.openingTimes.Monday.Open, Closed: bookingStartInfo.openingTimes.Monday.Closed } },
          { tuesday: { IsOpen: bookingStartInfo.openingTimes.Tuesday.IsOpen, Open: bookingStartInfo.openingTimes.Tuesday.Open, Closed: bookingStartInfo.openingTimes.Tuesday.Closed } },
          { wednesday: { IsOpen: bookingStartInfo.openingTimes.Wednesday.IsOpen, Open: bookingStartInfo.openingTimes.Wednesday.Open, Closed: bookingStartInfo.openingTimes.Wednesday.Closed } },
          { thursday: { IsOpen: bookingStartInfo.openingTimes.Thursday.IsOpen, Open: bookingStartInfo.openingTimes.Thursday.Open, Closed: bookingStartInfo.openingTimes.Thursday.Closed } },
          { friday: { IsOpen: bookingStartInfo.openingTimes.Friday.IsOpen, Open: bookingStartInfo.openingTimes.Friday.Open, Closed: bookingStartInfo.openingTimes.Friday.Closed } },
          { saturday: { IsOpen: bookingStartInfo.openingTimes.Saturday.IsOpen, Open: bookingStartInfo.openingTimes.Saturday.Open, Closed: bookingStartInfo.openingTimes.Saturday.Closed } }
        ],
        schedule: bookingStartInfo.schedule,
        liveresId: bookingStartInfo.liveresId,
        phone: bookingStartInfo.phone
      };
      setRestaurantInfo(restaurantInfo);
    }

    getSiteInfoId();
  }, []);

  const onSiteChangeHandler = (restaurantName, address, liveresId) => {
    setRestaurantInfo((restaurantInfo) => ({ ...restaurantInfo, restaurantName: restaurantName, address: address, liveresId: liveresId }));
  }



  const onDateChangeHandler = (day, month, year) => {
    bookingTimeModel.select(
      format(new Date(year, month, day), 'dd/MM/yyyy'),
      timeInfo.selectedHour.toString().padStart(2, '0') + ':' + timeInfo.selectedMinute.toString().padStart(2, '0')
    );
    setStatefromBookingTimeModel();
  }


  const onTimeChangeHandler = (hour, minute) => {
    bookingTimeModel.select(
      format(new Date(dateInfo.selectedYear, dateInfo.selectedMonth, dateInfo.selectedDay), 'dd/MM/yyyy'),
      hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0')
    );
    setStatefromBookingTimeModel();
  }

  useEffect(() => {
    guestInfo.numberOfGuests = bookingStartInfo.urlGuests ? bookingStartInfo.urlGuests : Math.max(2, bookingStartInfo.minimumNumberOfGuests || 2);
    guestInfo.firstName = "";
    guestInfo.lastName = "";
    guestInfo.email = "";
    guestInfo.phone = "";
    guestInfo.consent = false;
    guestInfo.ccg = "";
    guestInfo.captchaToken = "";
    guestInfo.consent = true;
    setGuestInfo(guestInfo);
  }, []);


  const onGuestChangeHandler = (numberOfGuests) => {
    setCheckingAvailability(true);
    setGuestInfo({ ...guestInfo, numberOfGuests: numberOfGuests })
  }

  const onGuestDetailsChangeHandler = (attr, val) => {
    setGuestInfo({ ...guestInfo, [attr]: val })
  }

  useEffect(() => {
    areaInfo.areas = [];
    areaInfo.selectedArea = "";
    areaInfo.areaMessage = "";
    areaInfo.ccgRequired = false;
    setAreaInfo(areaInfo);
  }, []);

  const onAreaChangeHandler = (areas, selectedArea) => {
    var thisArea = areas.filter(a => a.areaLinkGroup.areaLinks[0]?.name === selectedArea && a.name.toString().toLowerCase().indexOf('enquiry') == -1)[0];
    if (thisArea.requirements !== null && thisArea.requirements.isPreAuthRequired) {
      setAreaInfo({ ...areaInfo, areas: areas, selectedArea: thisArea, ccgRequired: true });
    }
    else {
      setAreaInfo({ ...areaInfo, areas: areas, selectedArea: thisArea, ccgRequired: false });
    }
      console.warn('onAreaChangeHandler', { ...areaInfo });

  }

  useEffect(() => {
    bookingProcess.step = 1;
    bookingProcess.ref = "";
    setBookingProcess(bookingProcess);
  },
    []);

  const onBookingProcessChangeHandler = (step) => {
    setCheckingAvailability(false);
    setBookingProcess({ ...bookingProcess, step: step })
  }



  useEffect(() => {
    availabilityOptions.venues = [];
    setAvailabilityOptions(availabilityOptions);
  },
    []);

  const onAvailabilityOptionsVenueChangeHandler = async (time, restaurantName, address, liveresId) => {
    var hour = time.toString().slice(0, 2);
    var minute = time.toString().slice(2);
    onTimeChangeHandler(hour, minute);
    if (restaurantName && address && liveresId) {
      await getUpdatedAreaData(liveresId, restaurantName, address);
    }

    onBookingProcessChangeHandler(2);
  }

  const onAvailabilityOptionsAreaChangeHandler = async (time, areaName) => {
    var hour = time.toString().slice(0, 2);
    var minute = time.toString().slice(2);
    onTimeChangeHandler(hour, minute);
    if (areaName) {
      var updatedArea = areaInfo.areas.filter(a => a.areaLinkGroup.areaLinks[0] && a.areaLinkGroup.areaLinks[0].name === areaName)[0];
        setAreaInfo({ ...areaInfo, selectedArea: updatedArea });
        console.warn('onAvailabilityOptionsAreaChangeHandler', { ...areaInfo });

    }

    onBookingProcessChangeHandler(2);
  }

  const ChangeToEnquiry = async () => {
    const enquiryOccasion = findEnquiryOccasion(areaInfo.areas, areaInfo.selectedArea);

    if (!enquiryOccasion) {
      console.error('Can not find Enquiry Occasion for Area ' + areaInfo.selectedArea?.areaLinkGroup?.areaLinks?.[0]?.id);
      return;
      }


      setAreaInfo({ ...areaInfo, selectedArea: enquiryOccasion });
      console.warn('enquiryOccasion', { ...areaInfo });
    onBookingProcessChangeHandler(7);
  }

  const handleBookingGoClick = async () => {
    setCheckingAvailability(true);
    var bookingDate = `${dateInfo.selectedYear}-${(dateInfo.selectedMonth + 1).toString().padStart(2, "0")}-${dateInfo.selectedDay.toString().padStart(2, "0")} ${timeInfo.selectedHour}:${timeInfo.selectedMinute}`;
    
    try {
      const request = new Request("/umbraco/Api/Booking/SearchAvailability/?venueId=" + restaurantInfo.liveresId + "&dateTime=" + bookingDate + "&guests=" + guestInfo.numberOfGuests + "&occasionId=" + areaInfo.selectedArea.id + "&areaId=" + areaInfo.selectedArea.areaLinkGroup.areaLinks[0].id);
      const response = await fetch(request);
      
      if (!response.ok) {
        setCreateBookingMessage("Unable to check availability at this time. Please try again or contact the restaurant directly.");
        setCheckingAvailability(false);
        return;
      }

      var responseJson = await response.json();
      
      // Handle null or empty response
      if (!responseJson) {
        setCreateBookingMessage("Unable to check availability at this time. Please try again or contact the restaurant directly.");
        setCheckingAvailability(false);
        return;
      }
      
      if (responseJson.originalChoiceAvailable) {
        onBookingProcessChangeHandler(2);
        setCheckingAvailability(false);
      }
      else if (!responseJson.originalChoiceAvailable && !responseJson.noAvailability) {
        var availableOptions = [];
        responseJson.venues.forEach(venue => {
          var newVenue = {};
          newVenue.restaurantName = venue.name;
          newVenue.availability = venue.availability;
          newVenue.liveresId = venue.liveresId;
          newVenue.phone = venue.telephone;
          newVenue.address = venue.address;
          newVenue.imageUrl = venue.imageUrl;
          newVenue.areaName = venue.areaName;
          availableOptions.push(newVenue);
        });
        setAvailabilityOptions(availableOptions);
        onBookingProcessChangeHandler(4);
        setCheckingAvailability(false);
      }
      else {
        onBookingProcessChangeHandler(5);
        setCheckingAvailability(false);
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setCreateBookingMessage("Unable to check availability at this time. Please try again or contact the restaurant directly.");
      setCheckingAvailability(false);
    }
  }

  const handleEnquiryGoClick = async () => {
    onBookingProcessChangeHandler(7);
  }

  const createBooking = async (token) => {
    setCheckingAvailability(true);
    setCreateBookingMessage(false);
    var bookingDate = `${dateInfo.selectedYear}-${(dateInfo.selectedMonth + 1).toString().padStart(2, "0")}-${dateInfo.selectedDay.toString().padStart(2, "0")} ${timeInfo.selectedHour}:${timeInfo.selectedMinute}`;
    var data = {
      LiveresId: restaurantInfo.liveresId,
      DateTime: bookingDate,
      Guests: guestInfo.numberOfGuests,
      Email: guestInfo.email,
      FirstName: guestInfo.firstName,
      LastName: guestInfo.lastName,
      Phone: guestInfo.phone,
      OptIn: guestInfo.consent,
      SpecialRequests: "",
      PaymentNonce: token ?? "",
      SystemNotes: "",
      SpecialAvailabilityCode: "",
      OccasionId: areaInfo.selectedArea.id,
      AreaId: areaInfo.selectedArea.areaLinkGroup.areaLinks[0].id,
      PreOrderRequired: areaInfo.selectedArea.name.toLowerCase().indexOf("pre") != -1 ? true : false,
      CaptchaToken: guestInfo.captchaToken
    };
    const request = new Request("/umbraco/Api/Booking/Create");
    try {
      const response = await fetch(request, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });

      setCheckingAvailability(false);

      if (!response.ok) {
        try {
          const errorJson = await response.json();
            setCreateBookingMessage(errorJson?.message || defaultBookingErrorMessage);
        } catch {
            setCreateBookingMessage(defaultBookingErrorMessage);
        }
        return;
      }

        var responseJson = await response.json();

      if (responseJson && responseJson.success) {
        window.location.href = `/edit-booking?bookingId=${responseJson.id}&bookingReference=${responseJson.bookingReference}`;
      } else {
          setCreateBookingMessage(responseJson?.message || defaultBookingErrorMessage);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      setCheckingAvailability(false);
        setCreateBookingMessage(defaultBookingErrorMessage);
    }
  }

  const createEnquiry = async () => {
    setCheckingAvailability(true);
    setCreateEnquiryError(null);
    var enquiryDate = `${dateInfo.selectedYear}-${(dateInfo.selectedMonth + 1).toString().padStart(2, "0")}-${dateInfo.selectedDay.toString().padStart(2, "0")} ${timeInfo.selectedHour}:${timeInfo.selectedMinute}`;
    var data = {
      LiveresId: restaurantInfo.liveresId,
      DateTime: enquiryDate,
      Guests: guestInfo.numberOfGuests,
      Email: guestInfo.email,
      FirstName: guestInfo.firstName,
      LastName: guestInfo.lastName,
      Phone: guestInfo.phone,
      OptIn: guestInfo.consent,
      SpecialRequests: guestInfo.specialRequest || "",
      PaymentNonce: "",
      SystemNotes: "",
      SpecialAvailabilityCode: "",
      OccasionId: areaInfo.selectedArea.id,
      AreaId: areaInfo.selectedArea.areaLinkGroup.areaLinks[0].id,
      CaptchaToken: guestInfo.captchaToken
    };
    try {
        const request = new Request("/umbraco/Api/Booking/CreateEnquiry");
        const response = await fetch(request, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(data)
        });
        
        if (!response.ok) {
          setCreateEnquiryError(defaultEnquiryErrorMessage);
          setCheckingAvailability(false);
          return;
        }

        var responseJson = await response.json();
        
        if (responseJson && responseJson.success) {
          setBookingProcess({ ...bookingProcess, ref: responseJson.bookingReference })
          setCheckingAvailability(false);
          onBookingProcessChangeHandler(8)
        } else {
          setCreateEnquiryError(responseJson?.message || defaultEnquiryErrorMessage);
          setCheckingAvailability(false);
        }
    } catch (error) {
        console.error('Error creating enquiry:', error);
        setCheckingAvailability(false);
        setCreateEnquiryError(defaultEnquiryErrorMessage);
    }
  }

  const isButtonValid = () => {
    if (dateInfo.selectedDay && timeInfo.selectedHour && timeInfo.selectedMinute && guestInfo.numberOfGuests > 0 && areaInfo.selectedArea) {
      return true;
    }
    return false;
  }

  useEffect(() => {
    setCheckingAvailability(true);
    const isOpenOnDay = (day) => day == null ? null : Object.values(day)[0]?.IsOpen;

    const getData = async () => {
      let newDate = new Date(Date.parse(`${dateInfo.selectedYear}-${(dateInfo.selectedMonth + 1).toString().padStart(2, '0')}-${dateInfo.selectedDay.toString().padStart(2, '0')}`));
      let day = newDate.getDay();
      let dayDifference = 0;
      while (!isOpenOnDay(restaurantInfo.openingTimes[day])) {
        day++
        dayDifference++;
        if (day > 6) day = 0;
        if (dayDifference > 7) return;
      }
      if (dayDifference > 0) {
        newDate.setDate(newDate.getDate() + dayDifference);
        setDateInfo({
          ...dateInfo,
          selectedYear: newDate.getFullYear(),
          selectedMonth: newDate.getMonth() + 1,
          selectedDay: newDate.getDate()
        });
        return;
      }

      var bookingDate = `${dateInfo.selectedYear}-${(dateInfo.selectedMonth + 1).toString().padStart(2, "0")}-${dateInfo.selectedDay.toString().padStart(2, "0")} ${timeInfo.selectedHour}:${timeInfo.selectedMinute}`;
      const request = new Request("/umbraco/Api/Booking/GetAvailableAreas/?venueId=" + restaurantInfo.liveresId + "&dateTime=" + bookingDate + "&guests=" + guestInfo.numberOfGuests + "&specialJourneys=" + !!bookingStartInfo.occasionBooking + "&occasionTypeFilter=" + (bookingStartInfo.occasionTypeFilter) + "&occasionId=" + (bookingStartInfo?.occasionBooking?.occasionId || ""));//?venueId=" + restaurantInfo.liveresId + "&dateTime=" + bookingDate + "&guests=" + guestInfo.numberOfGuests);
      const response = await fetch(request);
      var responseJson = await response.json();

      let newAreas = [];
      if (responseJson.occasions.length > 0) {
        responseJson.occasions.forEach(occasion => {
          newAreas.push(occasion);
        });

        var ccgRequired = false;
        if (responseJson.occasions.length === 1) {
          if (newAreas[0]?.requirements?.isPreAuthRequired) {
            ccgRequired = true;
          }
          setAreaInfo({ ...areaInfo, areas: newAreas, selectedArea: newAreas[0], ccgRequired });
        }
        else {
          var foundArea = newAreas.find(o => o.id == areaInfo?.selectedArea?.id) ||
            newAreas.find(o => o.areaLinkGroup.areaLinks.length > 0 && o.areaLinkGroup.areaLinks[0].name === areaInfo?.selectedArea?.areaLinkGroup?.areaLinks[0].name && o.name.indexOf('Enquiry') == -1);
          if (foundArea?.requirements?.isPreAuthRequired) {
            ccgRequired = true;
          }
          setAreaInfo({ ...areaInfo, areas: newAreas, selectedArea: foundArea, ccgRequired });
        }
      }
      else {
        setAreaInfo({ areas: [] });
      }
      setCheckingAvailability(false);
    }
    if (dateInfo.selectedDay && timeInfo.selectedHour && timeInfo.selectedMinute && guestInfo.numberOfGuests > 0) {
      getData();
    }
    else {
      //setAvailableAreas(null);
    }
  }, [
    dateInfo.selectedDay,
    timeInfo.selectedHour,
    timeInfo.selectedMinute,
    guestInfo.numberOfGuests,
    restaurantInfo,
  ]);

  const getUpdatedAreaData = async (liveresId, restaurantName, address) => {

    var bookingDate = `${dateInfo.selectedYear}-${(dateInfo.selectedMonth + 1).toString().padStart(2, "0")}-${dateInfo.selectedDay.toString().padStart(2, "0")} ${timeInfo.selectedHour}:${timeInfo.selectedMinute}`;
    const request = new Request("/umbraco/Api/Booking/GetAvailableAreas/?venueId=" + liveresId + "&dateTime=" + bookingDate + "&guests=" + guestInfo.numberOfGuests + "&specialJourneys=" + !!bookingStartInfo.occasionBooking + "&occasionTypeFilter=" + (bookingStartInfo.occasionTypeFilter) + "&occasionId=" + (bookingStartInfo?.occasionBooking?.occasionId || ""));//?venueId=" + restaurantInfo.liveresId + "&dateTime=" + bookingDate + "&guests=" + guestInfo.numberOfGuests);
    const response = await fetch(request);
    var responseJson = await response.json();

    let newAreas = [];
    responseJson.occasions.forEach(occasion => {
      newAreas.push(occasion);
    });
    var updatedArea = newAreas.filter(a => a.id === areaInfo.selectedArea.id)[0];
    onSiteChangeHandler(restaurantName, address, liveresId);
    setAreaInfo({ ...areaInfo, areas: newAreas, selectedArea: updatedArea });

  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    phone = phone.toString().replace("+", "").replace("44", "0")
    var phoneSplit = [phone.substring(0, 3), phone.substring(3, 7), phone.substring(7, 11)];
    var formattedNumber = phoneSplit.join(' ');
    return formattedNumber;
  }

  const progressBarWidth = (value) => {
    var progressWidth = "33.333%";
    switch (value) {
      case 1:
        progressWidth = "33.333%"
        break;
      case 2:
        progressWidth = "66.666%"
        break;
      case 4:
        progressWidth = "66.666%"
        break;
      case 3:
        progressWidth = "100%"
        break;
      case 6:
        progressWidth = "100%"
        break;

      default:
        break;
    }
    return progressWidth;
  }

  return (
    <form onSubmit={(e) => e.preventDefault()}>

      {(!isOccasionAvaliablity && !!bookingStartInfo.occasionBooking) && <div className="modal">
        <div className="container my-4 relative">
          <div className="bg-off-white shadow-lg lg:px-24 lg:py-12 md:px-12 md:py-6 p-4 relative overflow-auto">
            <h3 className="max-sm:mr-8">Booking unavailable</h3>
            <p>
              Sorry currently we are not taking bookings for {occasionBooking.occasionTitle} at {bookingStartInfo.name}. Please search again.
            </p>
            <div className="flex flex-row flex-wrap gap-4 mt-3 justify-center">
              <button type="button" className='button button-secondary button-lg text-center h-[56px]' onClick={() => history.back()}>
                <div className='flex flex-[1-1-100%] gap-2 text-center justify-center'>
                  <div>Search</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>}

      <div className="my-md">
        <div className='flex lg:flex-row flex-col gap-6'>
          <div>
            <p className='text-lg uppercase mb-0'>
              Booking Progress
            </p>
          </div>
          <div className='grow flex items-center'>
            <div className='w-full mx-0.5 h-1 bg-white relative'>
              <div className={`progress-dots left-0 is-active`}></div>
              <div className={`progress-dots left-[33.333%] ${bookingProcess.step >= 1 && 'is-active'}`}></div>
              <div className={`progress-dots left-[66.666%] ${(bookingProcess.step === 2 || bookingProcess.step === 4 || bookingProcess.step === 6) && 'is-active'}`}></div>
              <div className={`progress-dots left-[100%] ${bookingProcess.step === 6 && 'is-active'}`}></div>
              <div style={{ width: progressBarWidth(bookingProcess.step) }} className={`progress-bar`}></div>
            </div>
          </div>
        </div>
      </div>
      {/* Screen reader live region — announces step transitions */}
      <div id="booking-step-announcer" aria-live="polite" aria-atomic="true" className="sr-only">
        {stepAnnouncement}
      </div>

      <div className="my-md">

        {/* Restaurant info block — visible on steps 1 and 2 only */}
        <div className={![1, 2].includes(bookingProcess.step) ? 'booking-step--hidden' : ''} aria-hidden={![1, 2].includes(bookingProcess.step) || undefined}>
          <div className='pb-5'>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-5">
              <div className="md:col-span-3 lg:col-span-4">
                <p className='mb-0 text-xl font-[500]'>You're booking for</p>
              </div>
              <div className='col-span-1'>
                <div className='aspect-[16/9] overflow-hidden'>
                  <img className='object-cover' src={restaurantInfo.image} alt={restaurantInfo.imageAlt !== undefined ? restaurantInfo.imageAlt : restaurantInfo.restaurantName} />
                </div>
              </div>
              <div className='lg:col-span-2'>
                <div className='flex flex-col justify-between h-full'>
                  <div>
                    <h4 className='mb-2'>{restaurantInfo.restaurantName}</h4>
                    <p>{restaurantInfo.address}</p>
                    <a href={`tel:+${restaurantInfo.phone}`} className='link uppercase flex gap-2'>
                      <div className="box-icon">
                        <img className='w-5 h-5' src="/icons/restaurants/phone-call.svg" />
                      </div>
                      <div>
                        {formatPhoneNumber(restaurantInfo.phone)}
                      </div>
                    </a>
                  </div>
                  <div className='mt-6'>
                    <a className='link uppercase' href="/search">Change Restaurant</a>
                  </div>
                </div>
              </div>
            </div>
            <div className='h-px bg-sage w-full'></div>
          </div>
        </div>

        {/* Single BookingSummary — visible on steps 2, 4, 5, 6, 7, 8 */}
        <div className={![2, 4, 5, 6, 7, 8].includes(bookingProcess.step) ? 'booking-step--hidden' : ''} aria-hidden={![2, 4, 5, 6, 7, 8].includes(bookingProcess.step) || undefined}>
          <BookingSummary
            timeUnavailable={bookingProcess.step === 4}
            {...dateInfo}
            {...timeInfo}
            {...guestInfo}
            selectedArea={areaInfo.selectedArea}
            onChange={onBookingProcessChangeHandler}
          />
        </div>

        {/* Step 1 — Date / Time / Guests / Area */}
        <div
          ref={step1Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 1 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 1 || undefined}
        >
          <div className='grid md:grid-cols-2 lg:grid-cols-10 gap-6'>
            <div className='flex flex-col gap-4 lg:col-span-5'>
              <div>
                <DatePicker occasionBooking={occasionBooking} {...dateInfo} {...restaurantInfo} onChange={onDateChangeHandler}></DatePicker>
              </div>
              <div>
                <TimePicker hours={hours} minutes={minutes} {...dateInfo} {...timeInfo} openingTimes={restaurantInfo.openingTimes} schedule={bookingStartInfo.schedule} onChange={onTimeChangeHandler} occasionTitle={bookingStartInfo?.occasionBooking?.occasionTitle}></TimePicker>
              </div>
              <div>
                <GuestPicker
                  occasionBooking={occasionBooking}
                  {...guestInfo}
                  onChange={onGuestChangeHandler}
                  minimumNumberOfGuests={bookingStartInfo.minimumNumberOfGuests}
                />
              </div>
              <div>
                <div className={areaInfo.areas?.length > 1 ? '' : 'booking-step--hidden'} aria-hidden={areaInfo.areas?.length > 1 ? undefined : true}>
                  <AreaPicker occasionBooking={occasionBooking} {...dateInfo} {...timeInfo} {...guestInfo} {...areaInfo} onChange={onAreaChangeHandler}></AreaPicker>
                </div>
                {(!isCheckingAvailability && areaInfo.areas?.length == 0) && <div style={{ paddingTop: '8px' }}>No areas are available for booking with this date/time/guest combination. Please change your selection.</div>}
              </div>
              <div className='flex flex-col gap-4 lg:col-span-5'>
                {createBookingMessage && (
                  <div className="p-4 bg-tomato text-off-white">
                    <div className="flex gap-2">
                      <div className="box-icon items-center flex">
                        <img src="/icons/common/offwhite/alert-circle.svg" alt="alert" />
                      </div>
                      <div>{createBookingMessage}</div>
                    </div>
                  </div>
                )}
                {areaInfo?.selectedArea?.forceEnquiry ? <>
                  <button type="button" className='button button-secondary button-lg text-center h-[56px]' disabled={!isButtonValid() || isCheckingAvailability} onClick={handleEnquiryGoClick}>
                    <div className='flex flex-[1-1-100%] gap-2 text-center justify-center'>
                      <div>Enquire Now</div>
                      {isCheckingAvailability &&
                        <div className='box-icon flex flex-col justify-center'>
                          <div className="loader-small"></div>
                        </div>
                      }
                    </div>
                  </button>
                </> : <>
                  {guestInfo.numberOfGuests < 30 && <div className='flex pt-2'>
                    <button type="button" className='button button-secondary button-lg text-center h-[56px]' disabled={!isButtonValid() || isCheckingAvailability} onClick={handleBookingGoClick}>
                      <div className='flex flex-[1-1-100%] gap-2 text-center justify-center'>
                        <div>Check availability</div>
                        {isCheckingAvailability &&
                          <div className='box-icon flex flex-col justify-center'>
                            <div className="loader-small"></div>
                          </div>
                        }
                      </div>
                    </button>
                  </div>}
                  {guestInfo.numberOfGuests >= 30 && <div>
                    <p>Due to your party size being over 29 people we need to check availability.</p>
                    <button type="button" className='button button-secondary button-lg text-center h-[56px]' disabled={!isButtonValid() || isCheckingAvailability} onClick={handleEnquiryGoClick}>
                      <div className='flex flex-[1-1-100%] gap-2 text-center justify-center'>
                        <div>Enquire Now</div>
                        {isCheckingAvailability &&
                          <div className='box-icon flex flex-col justify-center'>
                            <div className="loader-small"></div>
                          </div>
                        }
                      </div>
                    </button>
                  </div>}
                </>}
              </div>
            </div>
            <div className='flex flex-col gap-4 lg:col-span-5'>
              {areaInfo?.selectedArea?.bookingStep1Content &&
                <div dangerouslySetInnerHTML={{ __html: areaInfo?.selectedArea?.bookingStep1Content }}>
                </div>
              }
            </div>
          </div>
        </div>

        {/* Step 2 — Guest details */}
        <div
          ref={step2Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 2 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 2 || undefined}
        >
          {createBookingMessage && (
            <div className="p-4 bg-tomato text-off-white mb-4">
              <div className="flex gap-2">
                <div className="box-icon items-center flex">
                  <img src="/icons/common/offwhite/alert-circle.svg" alt="alert" />
                </div>
                <div>{createBookingMessage}</div>
              </div>
            </div>
          )}
          <GuestDetails
            {...guestInfo}
            {...areaInfo}
            step={bookingProcess.step}
            onChange={onGuestDetailsChangeHandler}
            createBooking={createBooking}
            goToCcg={onBookingProcessChangeHandler}
            isCheckingAvailability={isCheckingAvailability}
            content={areaInfo?.selectedArea?.bookingGuestDetailsContent}
          />
        </div>

        {/* Step 4 — Alternate availability */}
        <div
          ref={step4Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 4 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 4 || undefined}
        >
          <AlternatePicker {...dateInfo} {...timeInfo} {...restaurantInfo} {...areaInfo} allOccasions={areaInfo.areas} options={availabilityOptions} onVenueChange={onAvailabilityOptionsVenueChangeHandler} onAreaChange={onAvailabilityOptionsAreaChangeHandler} onChangeToEnquiry={ChangeToEnquiry}></AlternatePicker>
        </div>

        {/* Step 5 — No availability */}
        <div
          ref={step5Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 5 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 5 || undefined}
        >
          <NoBookingsAvailable {...dateInfo} {...restaurantInfo} {...areaInfo} allOccasions={areaInfo.areas} onChangeToEnquiry={ChangeToEnquiry}></NoBookingsAvailable>
        </div>

        {/* Step 6 — Credit card capture */}
        <div
          ref={step6Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 6 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 6 || undefined}
        >
          <CreditCardCapture
            step={bookingProcess.step}
            occasionId={areaInfo?.selectedArea?.id}
            siteId={restaurantInfo.liveresId}
            onSubmit={createBooking}
            onChange={onGuestDetailsChangeHandler}
            isCheckingAvailability={isCheckingAvailability}
            content={areaInfo?.selectedArea?.bookingCCGDetailsContent}
            cardError={createBookingMessage}
            guestInfo={guestInfo}
            googleMerchantId={bookingStartInfo.googleMerchantId}
            environment={bookingStartInfo.environment}
          />
        </div>

        {/* Step 7 — Enquiry details */}
        <div
          ref={step7Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 7 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 7 || undefined}
        >
          <GuestEnquiryDetails
            occasionBooking={occasionBooking}
            guestInfo={{ ...guestInfo, specialRequest: guestInfo.specialRequest || "" }}
            areaInfo={areaInfo}
            onChange={onGuestDetailsChangeHandler}
            createEnquiry={createEnquiry}
            isCheckingAvailability={isCheckingAvailability}
            onGuestChange={onGuestChangeHandler}
            minimumNumberOfGuests={bookingStartInfo.minimumNumberOfGuests}
            guestNumberHelperText={guestNumberHelperText}
            enableCustomField={enableCustomField}
            customFieldLabel={customFieldLabel}
            errorMessage={createEnquiryError}
          />
        </div>

        {/* Step 8 — Enquiry confirmation */}
        <div
          ref={step8Ref}
          tabIndex={-1}
          className={bookingProcess.step !== 8 ? 'booking-step--hidden' : ''}
          aria-hidden={bookingProcess.step !== 8 || undefined}
        >
          <EnquiryConfirmation/>
        </div>

      </div>
    </form>)
}

export default BookingStart;